import { PROVINCES } from './constants.js';

/**
 * Pure data layer. No DOM, no D3 — turns the raw `historic_elecciones`
 * JSON into typed records consumed by the chart and tooltip.
 */

function getYears(raw) {
  return Object.keys(raw)
    .filter((k) => /^\d{4}$/.test(k))
    .sort();
}

function lookupPartyEntry(raw, year, idx) {
  return raw.partidos && raw.partidos[year + '-' + idx];
}

function partyName(raw, year, idx) {
  const e = lookupPartyEntry(raw, year, idx);
  return e ? e[0] : 'Otros';
}

function partyCode(raw, year, idx) {
  const e = lookupPartyEntry(raw, year, idx);
  return e && e[1] != null ? String(e[1]) : null;
}

function buildYearRecords(raw, year, names) {
  const yearData = raw[year];
  if (!yearData) return [];
  const out = [];
  for (const munCod of Object.keys(yearData)) {
    const entry = yearData[munCod];
    const d = Array.isArray(entry) ? entry[0] : entry;
    if (!d || !d.p || !d.v || d.p.length === 0) continue;

    // Drop records with implausible participación. The source occasionally
    // carries -1 or null for munis with no data; they would otherwise
    // plot on the negative x-axis edge.
    const tNum = +d.t;
    if (!isFinite(tNum) || tNum < 0) continue;

    const winnerIdx = d.p[0];
    const winnerN = partyName(raw, year, winnerIdx);
    const winnerC = partyCode(raw, year, winnerIdx);
    const winnerPct = +d.v[0] || 0;
    const secondPct = d.v.length > 1 ? +d.v[1] : 0;
    const secondN = d.p.length > 1 ? partyName(raw, year, d.p[1]) : null;

    const topParties = d.p.map((idx, k) => ({
      name: partyName(raw, year, idx),
      code: partyCode(raw, year, idx),
      pct: +d.v[k] || 0,
      votes: d.votos && d.votos[k] != null ? d.votos[k] : null
    }));

    const provCod = munCod.slice(0, 2);
    const nameEntry = names && names[munCod];

    out.push({
      munCod,
      year,
      name: nameEntry?.[0] || null,
      prov: nameEntry?.[1] || PROVINCES[provCod] || '',
      censo: +d.c || 0,
      participacion_pct: tNum,
      winner: winnerN,
      winnerCode: winnerC,
      winner_pct: winnerPct,
      second: secondN,
      second_pct: secondPct,
      margen_pct: Math.max(0, winnerPct - secondPct),
      winner_votes: d.votos?.[0] != null ? d.votos[0] : null,
      second_votes: d.votos?.[1] != null ? d.votos[1] : null,
      topParties
    });
  }
  return out;
}

/**
 * Pre-compute records for every year. ~787 munis × 12 years ≈ 9.5K
 * objects, negligible memory-wise.
 */
export function prepareAll(raw, names = {}) {
  const years = getYears(raw);
  const byYear = {};
  const all = [];
  for (const yr of years) {
    const recs = buildYearRecords(raw, yr, names);
    byYear[yr] = recs;
    for (const r of recs) all.push(r);
  }
  return { years, byYear, all };
}

/**
 * Collect the trajectory of a single muni across every year where it has
 * valid data. Used to draw the multi-year path when the user clicks a dot.
 */
export function getTrajectory(prepared, munCod) {
  const out = [];
  for (const yr of prepared.years) {
    const m = prepared.byYear[yr].find((r) => r.munCod === munCod);
    if (m) out.push(m);
  }
  return out;
}
